import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  ArrowRight, Microscope, BookOpen, Rocket, Newspaper, 
  Globe, Shield, Database, Brain, Cpu, BarChart2, Users, Clock, CheckCircle
} from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

const TabButton = ({ active, onClick, label, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-6 py-4 text-sm font-bold transition-all border-b-2 ${
      active 
        ? 'border-[#005a9c] text-[#005a9c] bg-[#005a9c]/5' 
        : 'border-transparent text-[#5c5c5c] hover:text-[#2c2c2c] hover:bg-slate-50'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

const VALID_TABS = ['applications', 'projects', 'publications', 'news'];

export default function ResearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'applications';
  const setActiveTab = (tab) => setSearchParams({ tab });

  const applications = [
    {
      title: 'Regulated Finance',
      desc: 'Developing techniques for LLM red teaming in high-compliance banking environments, focusing on data privacy and non-deterministic behavior.',
      icon: Shield
    },
    {
      title: 'Healthcare AI',
      desc: 'Applied research into differential privacy for medical AI models, ensuring research data remains secure and patient identities protected.',
      icon: Database
    },
    {
      title: 'Critical Infrastructure',
      desc: 'Securing edge-deployed AI models for industrial automation and predictive maintenance in power grids and water systems.',
      icon: Cpu
    }
  ];

  const projects = [
    {
      title: 'REINFORCE',
      status: 'Active',
      team: 'Digital Europe',
      desc: 'AI-Driven Cybersecurity for Hospitals and Healthcare Infrastructure. A €9M Digital Europe Programme project across 11 countries.',
      href: '/research/projects/reinforce'
    },
  ];

  const publications = [
    {
      title: 'Constant Modulus Waveforms for IoT-centric Integrated Sensing and Communications',
      authors: 'T. Han, S. Dayarathna, R. Senanayake, P. Smith, A. Kaushik, A. Mourad, R. S.-Gallacher and J. Evans',
      journal: 'IEEE Communications Standards Magazine',
      year: 'Mar. 2026',
      type: 'Early Access',
      url: 'https://arxiv.org/abs/2506.21078'
    },
    {
      title: 'Reconfigurable Holographic Surfaces and Near Field Communication for Non-Terrestrial Networks: Potential and Challenges',
      authors: 'M. A. Jamshed, M. A. Mohsin, H. Zhang, B. Haq, A. Kaushik, B. Di and W. Jiang',
      journal: 'IEEE Wireless Communications',
      year: 'Feb. 2026',
      type: 'Early Access',
      url: 'https://arxiv.org/abs/2509.08770'
    },
    {
      title: 'Adaptive Data Rate Optimization in Mobile and Dense LoRaWAN IoT Environments',
      authors: 'A. Gorrela, N. Choudhury, C. T. Calafate, W. Jiang, M. A. Jamshed and A. Kaushik',
      journal: 'IEEE Internet of Things Journal',
      year: 'Feb. 2026',
      type: 'Early Access',
      url: 'https://ieeexplore.ieee.org/document/11370172/'
    },
    {
      title: 'RIS-Enabled Joint Communications and Sensing in 6G NTNs: Opportunities and Challenges',
      authors: 'W. U. Khan, C. K. Sheemar, M. A. Jamshed, E. Lagunas, J. Querol, A. Kaushik and S. Chatzinotas',
      journal: 'IEEE Open Journal of the Communications Society, vol. 7, pp. 821–843',
      year: 'Jan. 2026',
      type: 'Journal Article',
      url: 'https://ieeexplore.ieee.org/document/11357516/'
    },
    {
      title: 'Integrated AI and 6G driven e-Health: A Trifecta for Smart Healthcare',
      authors: 'A. Dolas, R. Bodile, A. Kaushik, A. Kaur, R. Singh and P. Chatzimisios',
      journal: 'IEEE Communications Standards Magazine',
      year: 'Jan. 2026',
      type: 'Invited Paper · Early Access',
      url: null
    },
    {
      title: 'ISAC-Enhanced Robotic Coordination: A Tutorial on Joint Sensing-Communication Co-Design for Autonomous Multi-Agent Systems',
      authors: 'S. Sharma, A. Kaushik and R. Singh',
      journal: 'Foundations and Trends in Networking, vol. 15, no. 4, pp. 318–385',
      year: 'Jan. 2026',
      type: 'Invited Paper',
      url: 'https://www.emerald.com/ftnet/article/15/4/272/1334826/ISAC-enhanced-robotic-coordination-a-tutorial-on'
    },
    {
      title: 'Beamforming Design for Wideband Near-Field Communications With Reconfigurable Refractive Surfaces',
      authors: 'Z. Lin, S. Zeng, A. Kaushik and H. Zhang',
      journal: 'IEEE Transactions on Communications, vol. 73, no. 12, pp. 13369–13383',
      year: 'Dec. 2025',
      type: 'Journal Article',
      url: 'https://ieeexplore.ieee.org/document/11122459/'
    },
  ];

  const news = [
    {
      date: 'March 15, 2024',
      title: 'RakFort Joins Global AI Safety Partnership',
      category: 'Corporate'
    },
    {
      date: 'Feb 28, 2024',
      title: 'Breakthrough in Automated Prompt Injection Detection',
      category: 'Research'
    },
    {
      date: 'Jan 10, 2024',
      title: 'Annual AI Security Outlook Report Published',
      category: 'Insight'
    }
  ];

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <div className="rf-hero-band border-b border-[#e5eef6]">
        <RfPageFrame className="!py-16 md:!py-24">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#005a9c]/5 border border-[#005a9c]/10 text-[#005a9c] text-xs font-bold tracking-wider uppercase">
              <Microscope className="w-3 h-3" />
              Applied Research
            </div>
            <h1 className="text-4xl md:text-[3.5rem] font-bold text-[#2c2c2c] leading-[1.1] tracking-tight">
              Pioneering the science of <span className="text-[#005a9c]">AI safety and trust</span>
            </h1>
            <p className="text-[1.15rem] text-[#5c5c5c] leading-relaxed">
              Our research team bridges the gap between academic theory and enterprise reality, 
              validating new techniques in realistic settings to build a safer AI future.
            </p>
          </div>
        </RfPageFrame>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-[#e5eef6] sticky top-0 z-20">
        <RfPageFrame className="!py-0">
          <div className="flex flex-wrap md:flex-nowrap">
            <TabButton 
              active={activeTab === 'applications'} 
              onClick={() => setActiveTab('applications')} 
              label="Applications" 
              icon={Rocket} 
            />
            <TabButton 
              active={activeTab === 'projects'} 
              onClick={() => setActiveTab('projects')} 
              label="Our Projects" 
              icon={Brain} 
            />
            <TabButton 
              active={activeTab === 'publications'} 
              onClick={() => setActiveTab('publications')} 
              label="Scientific Publications" 
              icon={BookOpen} 
            />
            <TabButton 
              active={activeTab === 'news'} 
              onClick={() => setActiveTab('news')} 
              label="News" 
              icon={Newspaper} 
            />
          </div>
        </RfPageFrame>
      </div>

      {/* Tab Content */}
      <div className="bg-[#f0f7ff] py-16 md:py-24 min-h-[600px]">
        <RfPageFrame className="!py-0">
          
          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="grid md:grid-cols-3 gap-8">
              {applications.map((app) => (
                <div key={app.title} className="rf-card p-10 flex flex-col group hover:border-[#005a9c]/30 transition-all bg-white">
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center mb-8 border border-slate-100 group-hover:bg-[#005a9c] group-hover:text-white transition-all">
                    <app.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#2c2c2c] mb-4">{app.title}</h3>
                  <p className="text-[#5c5c5c] text-[0.95rem] leading-relaxed flex-1">
                    {app.desc}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div className="space-y-6 max-w-4xl">
              {projects.map((proj) => {
                const inner = (
                  <>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-[#005a9c]/10 text-[#005a9c] rounded">
                          {proj.status}
                        </span>
                        <span className="text-xs font-semibold text-slate-400 uppercase">{proj.team}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-[#2c2c2c] mb-3 group-hover:text-[#005a9c] transition-colors">{proj.title}</h3>
                      <p className="text-[#5c5c5c] leading-relaxed">{proj.desc}</p>
                    </div>
                    <div className="shrink-0">
                      <div className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center text-slate-300 group-hover:border-[#005a9c] group-hover:text-[#005a9c] transition-all">
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </div>
                  </>
                );
                return proj.href ? (
                  <Link
                    key={proj.title}
                    to={proj.href}
                    className="rf-card p-8 flex flex-col md:flex-row md:items-center gap-8 group hover:border-[#005a9c]/30 transition-all bg-white"
                  >
                    {inner}
                  </Link>
                ) : (
                  <div key={proj.title} className="rf-card p-8 flex flex-col md:flex-row md:items-center gap-8 group hover:border-[#005a9c]/30 transition-all bg-white">
                    {inner}
                  </div>
                );
              })}
            </div>
          )}

          {/* Publications Tab */}
          {activeTab === 'publications' && (
            <div className="grid md:grid-cols-2 gap-8">
              {publications.map((pub) => (
                <div key={pub.title} className="rf-card p-8 hover:border-[#005a9c]/30 transition-all bg-white">
                  <div className="flex flex-col h-full">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#005a9c] mb-4">
                      {pub.type} · {pub.year}
                    </div>
                    <h3 className="text-xl font-bold text-[#2c2c2c] mb-3 leading-tight">{pub.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed mb-2">{pub.authors}</p>
                    <div className="mt-auto flex items-center justify-between pt-6 border-t border-slate-100">
                      <span className="text-xs font-semibold text-slate-500 italic">{pub.journal}</span>
                      {pub.url && (
                        <a
                          href={pub.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#005a9c] text-xs font-bold flex items-center gap-1 hover:underline"
                        >
                          View <ArrowRight className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* News Tab */}
          {activeTab === 'news' && (
            <div className="space-y-8 bg-white p-10 rounded-2xl border border-slate-100 shadow-sm">
              {news.map((item) => (
                <div key={item.title} className="flex flex-col md:flex-row md:items-baseline gap-4 pb-8 border-b border-slate-100 last:border-0">
                  <div className="md:w-48 text-xs font-bold text-slate-400 uppercase tracking-wider shrink-0">{item.date}</div>
                  <div className="flex-1 space-y-2">
                    <div className="text-[10px] font-bold text-[#005a9c] uppercase tracking-tighter">{item.category}</div>
                    <h3 className="text-xl font-bold text-[#2c2c2c] hover:text-[#005a9c] cursor-pointer transition-colors">{item.title}</h3>
                  </div>
                  <div className="md:w-32 text-right">
                    <ArrowRight className="w-5 h-5 text-slate-200" />
                  </div>
                </div>
              ))}
            </div>
          )}

        </RfPageFrame>
      </div>

      {/* R&D Ethos Section */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 opacity-10 translate-x-1/4 -translate-y-1/4">
          <Globe className="w-96 h-96" />
        </div>
        <RfPageFrame>
          <div className="max-w-3xl relative z-10 space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold">Our Global R&D Collaboration</h2>
            <p className="text-slate-400 text-lg leading-relaxed">
              We participate in collaborative R&D programs with universities and industrial partners across the 
              USA, Ireland, UK, and Italy. By validating emerging techniques in realistic multi-disciplinary settings, 
              we ensure our enterprise products remain at the cutting edge of the field.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 pt-8">
              {[
                'Multi-disciplinary teams',
                'Ethical validation',
                'Open-source contribution',
                'Peer-reviewed rigor',
                'Enterprise applicability',
                'Emerging threat focus'
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#005a9c]" />
                  <span className="text-sm font-semibold text-slate-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </RfPageFrame>
      </section>

      {/* Research CTA */}
      <section className="py-24 bg-[#005a9c] text-white">
        <RfPageFrame className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">Partner with our Research Labs</h2>
          <p className="text-blue-100 text-lg mb-12 max-w-2xl mx-auto leading-relaxed">
            Interested in collaborating on a research project or accessing our technical publications? 
            Our research team is always open to dialogue with practitioners and academic institutions.
          </p>
          <a
            href="/contactus"
            className="px-10 py-4 bg-white text-[#005a9c] font-bold rounded-lg hover:bg-slate-50 transition-all shadow-xl inline-block"
          >
            Inquire about collaboration
          </a>
        </RfPageFrame>
      </section>
    </div>
  );
}
